import { useState, useEffect } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight, Eye, UserX, UserCheck, Loader2, Trash2, Plus, Wallet, AlertTriangle } from 'lucide-react';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDesc, setCreditDesc] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);

  const pageSize = 20;

  const loadUsers = async () => {
    try {
      const res = await adminApi.users({ skip: page * pageSize, limit: pageSize, search });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch {}
  };

  useEffect(() => { loadUsers(); }, [page, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewUser = async (userId) => {
    setLoading(true);
    try {
      const [userRes, walletRes] = await Promise.all([
        adminApi.userDetail(userId),
        adminApi.getUserWallet(userId).catch(() => ({ data: { wallet: { balance: 0 }, transactions: [] } }))
      ]);
      setDetail({ ...userRes.data, wallet: walletRes.data.wallet, wallet_transactions: walletRes.data.transactions });
      setSelected(userId);
    } catch {}
    setLoading(false);
  };

  const updateUser = async (userId, updates) => {
    try {
      await adminApi.updateUser(userId, updates);
      toast.success('User updated');
      loadUsers();
      if (selected === userId) viewUser(userId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const handleCredit = async (userId) => {
    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      const res = await adminApi.creditWallet(userId, { amount, description: creditDesc || 'Admin credit' });
      toast.success(`Credited $${amount.toFixed(2)}. New balance: $${res.data.new_balance.toFixed(2)}`);
      setCreditAmount(''); setCreditDesc('');
      loadUsers();
      if (selected === userId) viewUser(userId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Credit failed'); }
  };

  const startDelete = (user) => {
    setDeleteTarget(user);
    setDeleteStep(1);
    setConfirmEmail('');
  };

  const executeDelete = async () => {
    if (!deleteTarget || confirmEmail !== deleteTarget.email) return;
    setDeleting(true);
    try {
      const res = await adminApi.deleteUser(deleteTarget._id, { confirm_email: confirmEmail });
      toast.success(`User ${deleteTarget.email} permanently deleted`);
      setDeleteStep(0); setDeleteTarget(null); setConfirmEmail('');
      setSelected(null); setDetail(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deletion failed');
    }
    setDeleting(false);
  };

  const currentAdmin = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div data-testid="user-management" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">User Management</h2>
        <p className="text-muted-foreground mt-1">{total} total users</p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="user-search" placeholder="Search by email, name, or company..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} className="pl-9" />
        </div>
      </div>

      <Card className="bg-card border-border/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50 text-muted-foreground bg-secondary/20">
                  <th className="text-left py-3 px-4 font-medium">Email</th>
                  <th className="text-left py-3 px-4 font-medium">Name</th>
                  <th className="text-left py-3 px-4 font-medium">Country</th>
                  <th className="text-right py-3 px-4 font-medium">Balance</th>
                  <th className="text-left py-3 px-4 font-medium">Status</th>
                  <th className="text-left py-3 px-4 font-medium">Services</th>
                  <th className="text-left py-3 px-4 font-medium">Joined</th>
                  <th className="text-right py-3 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isSelf = u._id === currentAdmin?.sub || u.email === currentAdmin?.email;
                  return (
                    <tr key={u._id} data-testid={`user-row-${u._id}`} className="border-b border-border/20 hover:bg-secondary/10 transition-colors">
                      <td className="py-2.5 px-4 font-mono text-xs">{u.email}</td>
                      <td className="py-2.5 px-4">{u.full_name}</td>
                      <td className="py-2.5 px-4 text-xs">{u.signup_country_code || ''} {u.signup_country || ''}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-xs">
                        <span className={u.wallet_balance > 0 ? 'text-emerald-400' : u.wallet_balance === 0 ? 'text-muted-foreground' : 'text-destructive'}>
                          ${(u.wallet_balance || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="py-2.5 px-4">
                        <Badge className={u.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-destructive/20 text-destructive'}>{u.is_active ? 'Active' : 'Inactive'}</Badge>
                        {u.is_admin && <Badge className="ml-1 bg-primary/20 text-primary text-[10px]">Admin</Badge>}
                      </td>
                      <td className="py-2.5 px-4 font-mono text-xs">{u.enabled_services?.filter(s => s.is_active).length || 0}</td>
                      <td className="py-2.5 px-4 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                      <td className="py-2.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => viewUser(u._id)} className="h-7 w-7 p-0" title="View detail"><Eye className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => { setSelected(u._id); setCreditAmount('50'); setCreditDesc('Admin credit'); viewUser(u._id); }} className="h-7 w-7 p-0 text-emerald-400 hover:bg-emerald-500/10" title="Add credit"><Plus className="w-3 h-3" /></Button>
                          {u.is_active ? (
                            <Button size="sm" variant="ghost" onClick={() => updateUser(u._id, { is_active: false })} className="h-7 w-7 p-0 text-amber-400 hover:bg-amber-500/10" title="Deactivate"><UserX className="w-3 h-3" /></Button>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => updateUser(u._id, { is_active: true })} className="h-7 w-7 p-0 text-emerald-400 hover:bg-emerald-500/10" title="Activate"><UserCheck className="w-3 h-3" /></Button>
                          )}
                          {!isSelf && (
                            <Button size="sm" variant="ghost" onClick={() => startDelete(u)} className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10" title="Delete user"><Trash2 className="w-3 h-3" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > pageSize && (
            <div className="flex items-center justify-between p-3 border-t border-border/50">
              <span className="text-xs text-muted-foreground">Page {page + 1} of {Math.ceil(total / pageSize)}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="h-7"><ChevronLeft className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" disabled={(page + 1) * pageSize >= total} onClick={() => setPage(p => p + 1)} className="h-7"><ChevronRight className="w-3 h-3" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setDetail(null); setCreditAmount(''); setCreditDesc(''); setResetPassword(''); }}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>User Detail</DialogTitle></DialogHeader>
          {loading || !detail ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Email:</span> <span className="font-mono">{detail.user?.email}</span></div>
                <div><span className="text-muted-foreground">Name:</span> {detail.user?.full_name}</div>
                <div><span className="text-muted-foreground">Company:</span> {detail.user?.company_name}</div>
                <div><span className="text-muted-foreground">Country:</span> {detail.user?.signup_country} ({detail.user?.signup_country_code})</div>
                <div><span className="text-muted-foreground">City:</span> {detail.user?.signup_city}</div>
              </div>

              {/* Wallet Section */}
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-primary" />
                    <span className="font-medium">Wallet</span>
                  </div>
                  <span className={`text-lg font-bold font-mono ${(detail.wallet?.balance || 0) > 0 ? 'text-emerald-400' : 'text-destructive'}`}>
                    ${(detail.wallet?.balance || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Input placeholder="Amount" type="number" min="1" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} className="w-24 h-8 text-xs font-mono" />
                  <Input placeholder="Description" value={creditDesc} onChange={e => setCreditDesc(e.target.value)} className="flex-1 h-8 text-xs" />
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleCredit(selected)} disabled={!creditAmount}>
                    <Plus className="w-3 h-3 mr-1" />Credit
                  </Button>
                </div>
                {detail.wallet_transactions?.length > 0 && (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {detail.wallet_transactions.slice(0, 8).map((t, i) => (
                      <div key={i} className="flex justify-between text-[11px] py-1 border-b border-border/10 last:border-0">
                        <span className="text-muted-foreground truncate mr-2">{t.description?.slice(0, 40)}</span>
                        <span className={`font-mono shrink-0 ${t.type === 'credit' ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {t.type === 'credit' ? '+' : '-'}${t.amount?.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {detail.user?.enabled_services?.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-medium mb-2">Enabled Services</h4>
                    <div className="space-y-1">
                      {detail.user.enabled_services.filter(s => s.is_active).map(s => (
                        <div key={s.service} className="flex justify-between p-2 rounded bg-secondary/20 text-xs">
                          <span>{s.service}</span><Badge variant="outline" className="text-[10px]">{s.latency_tier}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Password Reset */}
              <Separator />
              <div className="space-y-2">
                <p className="text-xs font-medium flex items-center gap-1.5">Set Password</p>
                <p className="text-[10px] text-muted-foreground">Set a temporary password for this user. They can change it after logging in.</p>
                <div className="flex gap-2">
                  <Input data-testid="admin-reset-password" type="text" placeholder="New password (min 6 chars)" value={resetPassword} onChange={e => setResetPassword(e.target.value)} className="flex-1 h-8 text-xs font-mono" />
                  <Button size="sm" className="h-8 text-xs" disabled={!resetPassword || resetPassword.length < 6} onClick={async () => {
                    try {
                      await adminApi.updateUser(selected, { new_password: resetPassword });
                      toast.success('Password reset successfully. User can log in with the new password.');
                      setResetPassword('');
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
                  }}>Set Password</Button>
                </div>
              </div>

              {/* Danger Zone */}
              <Separator />
              <div className="space-y-2">
                <p className="text-xs font-bold text-destructive uppercase tracking-wider">Danger Zone</p>
                <Button variant="outline" size="sm" className="w-full border-destructive/30 text-destructive hover:bg-destructive/10"
                  onClick={async () => {
                    if (!window.confirm(`Delete ALL namespaced data for ${detail.user?.email}? The account will remain.`)) return;
                    try { await adminApi.deleteUserData(selected); toast.success('User data deleted from GPU server'); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }}>
                  <Trash2 className="w-3 h-3 mr-1.5" />Delete GPU Server Data Only (GDPR)
                </Button>
                {detail.user?.email !== currentAdmin?.email && (
                  <Button variant="outline" size="sm" className="w-full border-destructive/50 bg-destructive/5 text-destructive hover:bg-destructive/15 font-bold"
                    onClick={() => startDelete(detail.user)}>
                    <AlertTriangle className="w-3 h-3 mr-1.5" />Delete User Account Permanently
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog — Step 1 */}
      <Dialog open={deleteStep === 1} onOpenChange={() => { setDeleteStep(0); setDeleteTarget(null); }}>
        <DialogContent className="bg-card border-destructive/30 max-w-md">
          <DialogHeader><DialogTitle className="text-destructive flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Delete User Permanently</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Are you sure you want to permanently delete <strong className="font-mono">{deleteTarget?.email}</strong>?</p>
            <div className="text-xs text-muted-foreground space-y-1 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
              <p>This will <strong>permanently remove</strong>:</p>
              <p>- Their account and all API keys</p>
              <p>- All data on the GPU server (Qdrant collections, ES indices, Neo4j nodes, Redis keys, MinIO buckets)</p>
              <p>- All usage records, wallet balance (${(deleteTarget?.wallet_balance || 0).toFixed(2)}), and payment history</p>
              <p className="font-bold text-destructive mt-2">This action is IRREVERSIBLE.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setDeleteStep(0); setDeleteTarget(null); }}>Cancel</Button>
              <Button className="bg-destructive hover:bg-destructive/90 text-white" onClick={() => setDeleteStep(2)}>Continue</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog — Step 2: Type email */}
      <Dialog open={deleteStep === 2} onOpenChange={() => { setDeleteStep(0); setDeleteTarget(null); setConfirmEmail(''); }}>
        <DialogContent className="bg-card border-destructive/30 max-w-md">
          <DialogHeader><DialogTitle className="text-destructive">Confirm Deletion</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Type the user's email to confirm:</p>
            <p className="text-xs font-mono text-muted-foreground bg-secondary/30 p-2 rounded">{deleteTarget?.email}</p>
            <Input data-testid="confirm-delete-email" placeholder="Type email here..." value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} className="font-mono text-sm" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setDeleteStep(0); setDeleteTarget(null); setConfirmEmail(''); }}>Cancel</Button>
              <Button data-testid="confirm-delete-btn" className="bg-destructive hover:bg-destructive/90 text-white" onClick={executeDelete}
                disabled={confirmEmail !== deleteTarget?.email || deleting}>
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Permanently'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
